/**
 * **La duración del webm** («Grabar mejora»). Chrome graba el webm «en vivo»: la cabecera sale sin
 * `Duration` (y sin índice), y el reproductor muestra ∞, una duración calculada por el peso (VLC,
 * el de Windows) o no deja adelantar. Por eso un video de 8 minutos «se veía de 4» (H6, 20-oct).
 *
 * Aquí se escribe la duración real en `Segment › Info`, sin librerías: solo se tocan los primeros
 * 64 KB del archivo (la cabecera), el resto viaja igual. Si el archivo no es un webm que se pueda
 * arreglar con seguridad (mp4 de Safari, un índice `SeekHead` que habría que recalcular), queda
 * como está.
 */

const ID = {
  ebml: 0x1a45dfa3,
  segment: 0x18538067,
  info: 0x1549a966,
  seekHead: 0x114d9b74,
  cluster: 0x1f43b675,
  timecodeScale: 0x2ad7b1,
  duration: 0x4489,
};
const CABEZA = 64 * 1024;

type Elemento = { id: number; inicio: number; largoId: number; largoTamano: number; datos: number; tamano: number; desconocido: boolean };

/** Un número EBML de largo variable en `i`: el ID conserva su marca; el tamaño, no. */
function vint(b: Uint8Array, i: number, conMarca: boolean) {
  const primero = b[i];
  if (primero === undefined) return null;
  let largo = 1;
  while (largo <= 8 && !(primero & (0x80 >> (largo - 1)))) largo++;
  if (largo > 8 || i + largo > b.length) return null;
  const mascara = 0xff >> largo;
  let valor = conMarca ? primero : primero & mascara;
  let unos = (primero & mascara) === mascara;
  for (let k = 1; k < largo; k++) {
    const byte = b[i + k] ?? 0;
    valor = valor * 256 + byte;
    if (byte !== 0xff) unos = false;
  }
  return { largo, valor, unos };
}

function elemento(b: Uint8Array, i: number): Elemento | null {
  const id = vint(b, i, true);
  const tamano = id && vint(b, i + id.largo, false);
  if (!id || !tamano) return null;
  const datos = i + id.largo + tamano.largo;
  return { id: id.valor, inicio: i, largoId: id.largo, largoTamano: tamano.largo, datos, tamano: tamano.valor, desconocido: tamano.unos };
}

/** El tamaño `valor` en `largo` bytes, o `null` si no cabe (el todo-unos es «desconocido»). */
function tamanoEn(valor: number, largo: number): Uint8Array<ArrayBuffer> | null {
  if (largo > 8 || valor > Number.MAX_SAFE_INTEGER || valor >= 2 ** (7 * largo) - 1) return null;
  const out = new Uint8Array(largo);
  for (let k = largo - 1, v = valor; k >= 0; k--, v = Math.floor(v / 256)) out[k] = v % 256;
  out[0] = (out[0] ?? 0) | (0x80 >> (largo - 1));
  return out;
}

function uint(b: Uint8Array, i: number, n: number) {
  let v = 0;
  for (let k = 0; k < n; k++) v = v * 256 + (b[i + k] ?? 0);
  return v;
}

/**
 * La cabecera `b` con la duración `ms` escrita en `Info` (la reemplaza si ya traía una), o `null`
 * si no es un webm que se pueda arreglar sin riesgo.
 */
export function fijarDuracion(b: Uint8Array, ms: number): Uint8Array<ArrayBuffer> | null {
  const ebml = elemento(b, 0);
  if (!ebml || ebml.id !== ID.ebml) return null;
  const segmento = elemento(b, ebml.datos + ebml.tamano);
  if (!segmento || segmento.id !== ID.segment) return null;
  for (let i = segmento.datos; ;) {
    const el = elemento(b, i);
    // Un `SeekHead` apunta a posiciones que se correrían; un `Cluster` antes de `Info` no es lo esperado.
    if (!el || el.desconocido || el.id === ID.cluster || el.id === ID.seekHead) return null;
    if (el.id === ID.info) return conDuracion(b, segmento, el, ms);
    i = el.datos + el.tamano;
  }
}

function conDuracion(b: Uint8Array, segmento: Elemento, info: Elemento, ms: number): Uint8Array<ArrayBuffer> | null {
  const fin = info.datos + info.tamano;
  if (fin > b.length) return null;
  let escala = 1_000_000;
  let previa: Elemento | null = null;
  for (let j = info.datos; j < fin;) {
    const hijo = elemento(b, j);
    if (!hijo) return null;
    if (hijo.id === ID.timecodeScale) escala = uint(b, hijo.datos, hijo.tamano) || escala;
    if (hijo.id === ID.duration) previa = hijo;
    j = hijo.datos + hijo.tamano;
  }
  // La duración va en «ticks» de TimecodeScale nanosegundos (por defecto, 1 ms).
  const valor = (ms * 1_000_000) / escala;
  if (previa) {
    if (previa.tamano !== 4 && previa.tamano !== 8) return null;
    const copia = b.slice();
    const vista = new DataView(copia.buffer);
    if (previa.tamano === 8) vista.setFloat64(previa.datos, valor);
    else vista.setFloat32(previa.datos, valor);
    return copia;
  }
  const duracion = new Uint8Array(11);
  duracion.set([0x44, 0x89, 0x88]);
  new DataView(duracion.buffer).setFloat64(3, valor);
  const tamanoInfo = tamanoEn(info.tamano + 11, info.largoTamano) ?? tamanoEn(info.tamano + 11, info.largoTamano + 1);
  if (!tamanoInfo) return null;
  const crece = tamanoInfo.length - info.largoTamano + duracion.length;
  // Chrome deja el tamaño del segmento «desconocido»; si viene escrito, se corrige en el mismo largo.
  const tamanoSegmento = segmento.desconocido
    ? b.subarray(segmento.inicio + segmento.largoId, segmento.datos)
    : tamanoEn(segmento.tamano + crece, segmento.largoTamano);
  if (!tamanoSegmento) return null;
  const partes = [
    b.subarray(0, segmento.inicio + segmento.largoId),
    tamanoSegmento,
    b.subarray(segmento.datos, info.inicio + info.largoId),
    tamanoInfo,
    b.subarray(info.datos, fin),
    duracion,
    b.subarray(fin),
  ];
  const out = new Uint8Array(partes.reduce((n, p) => n + p.length, 0));
  partes.reduce((pos, p) => (out.set(p, pos), pos + p.length), 0);
  return out;
}

/** El archivo con su duración real en la cabecera; si no se puede, el mismo archivo. */
export async function conDuracionReal(archivo: Blob, ms: number): Promise<Blob> {
  if (!archivo.type.endsWith("/webm") || ms <= 0) return archivo;
  try {
    const cabeza = new Uint8Array(await archivo.slice(0, CABEZA).arrayBuffer());
    const fija = fijarDuracion(cabeza, ms);
    return fija ? new Blob([fija, archivo.slice(CABEZA)], { type: archivo.type }) : archivo;
  } catch {
    return archivo;
  }
}
